import { GlobalHeader } from "@cmsgov/ds-medicare-gov";
import React, { useContext } from "react";
import { AppStore } from "../../app/store";
import { RouteComponentProps, withRouter } from "react-router";
import { UserLanguage, ActionType, MctToastType } from "../../@types";
import {
  sendTealiumEvent,
  useTranslate,
  logError,
  getBeneFirstNameAndLastInitial,
  shouldDisplayGlobalHeader,
} from "../../helpers";
import { hideGlobalHeaderPaths } from "../../app/routes";
import URI from "urijs";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  hasLoginSession,
  initiateLogin,
  getSlsxSignoutUrl,
} from "../../helpers/loginHelpers";
import { sendLogoutRequest } from "../../api";
import { BeneficiaryInfo } from "../";
import { getRouteName } from "../../helpers/routeHelpers";
import { launchChat } from "../../helpers/chatHelpers";

interface HeaderProps {
  productName?: string;
  productLink?: string;
  noBorder?: boolean;
  variation?: string;
  excludeUserInfo?: boolean;
  hideLogin?: boolean;
}

const Header: React.FunctionComponent<HeaderProps & RouteComponentProps> = ({
  productName,
  productLink,
  noBorder,
  variation,
  history,
  location,
  excludeUserInfo,
  hideLogin,
}) => {
  // Determine whether the global (consistent) header should be displayed,
  // based on the current route - - Also see `/app/MaxGlobalHeader.tsx`
  const displayGlobalHeader = shouldDisplayGlobalHeader(
    location,
    hideGlobalHeaderPaths
  );
  const {
    state: { csr, beneficiary, language },
    dispatch,
  } = useContext(AppStore.AppContext);

  const t = useTranslate();
  const {
    feEnableAuthentication,
    feEnableChat,
    tmpFeEnableMaxGlobalHeader,
  } = useFlags();

  const isEnglish = language === UserLanguage.ENGLISH;

  const switchLanguage = (): void => {
    const routeName = getRouteName(location.pathname);

    sendTealiumEvent(
      routeName,
      `Header - Toggle: ${isEnglish ? "Espanol" : "English"}`
    );

    const languageSwitch = isEnglish
      ? UserLanguage.SPANISH
      : UserLanguage.ENGLISH;

    dispatch({
      type: ActionType.UPDATE_LANGUAGE,
      payload: languageSwitch,
    });

    const uri = new URI(location.search);
    uri.setQuery(
      "lang",
      isEnglish ? UserLanguage.SPANISH : UserLanguage.ENGLISH
    );
    location.search = uri.search();
    history.replace(location);
  };

  const loggedIn = hasLoginSession() && beneficiary;

  const logout = async (): Promise<void> => {
    try {
      await sendLogoutRequest();
    } catch (e) {
      logError("Could not log out user (LoginLogoutButton)", e as Error);
    }

    history.push(`/?mctt=${MctToastType.LOGGED_OUT}`);

    dispatch({ type: ActionType.RESET_STATE });

    if (!csr) {
      const signoutUrl = getSlsxSignoutUrl();
      window.location.href = signoutUrl;
    }
  };

  const login = (): void => {
    sendTealiumEvent("Header - Log in", "Login");
    initiateLogin(language === UserLanguage.SPANISH, "", undefined);
  };

  let loginButtonText = "";
  let loginFunction = (): void => {};

  // It's important to check if this variable is false because it can be undefined initially
  if (feEnableAuthentication === false && !loggedIn) {
    loginButtonText = t("app.login_unavailable");
  } else if (loggedIn) {
    loginButtonText = t("app.menu.logout");
    loginFunction = logout;
  } else {
    loginButtonText = t("app.menu.login");
    loginFunction = login;
  }

  const actions = [];

  if (!excludeUserInfo && beneficiary) {
    actions.push({
      text: getBeneFirstNameAndLastInitial(beneficiary, t),
      menuPanel: <BeneficiaryInfo bene={beneficiary} />,
    });
  }

  if (loggedIn && feEnableChat) {
    actions.push({
      text: (
        <>
          <i className="fas fa-comments ds-u-margin-right--1" />
          {t("app.menu.live_chat")}
        </>
      ),
      callback: (): Promise<boolean> => launchChat(language, location.pathname),
    });
  }

  if (!hideLogin) {
    actions.push({
      text: loginButtonText,
      callback: loginFunction,
      props: { id: "mct-login-button" },
    });
  }

  actions.push({
    text: isEnglish ? "Español" : "English",
    callback: switchLanguage,
  });

  return !tmpFeEnableMaxGlobalHeader && displayGlobalHeader ? (
    <GlobalHeader
      logoCallback={(): void => {
        const routeName = getRouteName(location.pathname);

        sendTealiumEvent(routeName, "Header: Logo");
      }}
      productName={productName}
      productLink={productLink}
      variation={variation}
      noBorder={noBorder}
      actions={actions}
      menuButtonAriaLabel={t("navigation_menu")}
    />
  ) : null;
};

export default withRouter(Header);
